import React, { useState } from 'react';
import './ShareModal.scss';
import { QRCodeCanvas } from "qrcode.react";

import { Constants } from "@eagerdog/constants";
import { ShareChannel } from "@eagerdog/interfaces";

import shareIcon from '../../assets/icons/share.svg';
import facebookIcon from '../../assets/icons/facebook.svg';
import xIcon from '../../assets/icons/x.svg';
import whatsappIcon from '../../assets/icons/whatsapp.svg';
import emailIcon from '../../assets/icons/email.svg';

interface IProps {
  eventTitle: string;
  eventUrl: string;
  shareType?: 'event' | 'club' | 'dog' | 'post';
  /** Short detail appended to `eventTitle` on channels that accept text (X, WhatsApp, email, native sheet). Without it the message is a sentence built from `shareType` + `eventTitle`. */
  shareText?: string;
  onClose: () => void;
  /** Fired whenever a share action actually completes. Optional — most callers don't track this. */
  onShare?: (channel: ShareChannel) => void;
}

/**
 * Share sheet. Facebook only ever shows a link card built from the page's
 * og:* tags (it ignores any text we send), so the URL has to unfurl — see
 * FEED_POST_DEEP_LINK_PRD.md. Every other channel gets `text` + `url`.
 * The native sheet (navigator.share) is the route to Messenger, Instagram DMs
 * and iMessage; it is offered whenever the browser supports it.
 */
const ShareModal: React.FC<IProps> = ({ eventTitle, eventUrl, shareType = 'event', shareText, onClose, onShare }) => {
  const [copySuccess, setCopySuccess] = useState<boolean>(false);
  const canNativeShare = typeof navigator !== "undefined" && typeof navigator.share === "function";

  const defaultText = (): string => {
    switch (shareType) {
      case 'dog':
        return `Check out ${eventTitle} on Eager Dog!`;
      case 'club':
        return `Check out ${eventTitle} on Eager Dog`;
      case 'post':
        return `${eventTitle} on Eager Dog`;
      case 'event':
      default:
        return `Check out this dog event: ${eventTitle}`;
    }
  };
  // "Tango earned Novice Agility — AKC Agility · Nov 2" rather than a generic "Check out this".
  const text = shareText ? `${eventTitle} — ${shareText}` : defaultText();

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(eventUrl);
      setCopySuccess(true);
      onShare?.(Constants.share_channel.copy_link);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const handleNativeShare = async () => {
    try {
      await navigator.share({ title: eventTitle, text, url: eventUrl });
      onShare?.(Constants.share_channel.native);
    } catch (err) {
      // AbortError = the person closed the sheet; nothing to report.
      if ((err as DOMException)?.name !== "AbortError") {
        console.error('Native share failed:', err);
      }
    }
  };

  const handleSocialShare = (channel: ShareChannel) => {
    const url = encodeURIComponent(eventUrl);
    let shareUrl = '';

    switch (channel) {
      case Constants.share_channel.facebook:
        // Facebook reads og:* from the URL; it ignores any text parameter.
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
        break;
      case Constants.share_channel.x: {
        const hashtags = shareType === 'dog' || shareType === 'post' ? 'DogSports,EagerDog' : 'DogEvents,DogShows';
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${url}&hashtags=${hashtags}`;
        break;
      }
      case Constants.share_channel.whatsapp:
        shareUrl = `https://wa.me/?text=${encodeURIComponent(`${text}\n${eventUrl}`)}`;
        break;
      case Constants.share_channel.email:
        shareUrl = `mailto:?subject=${encodeURIComponent(eventTitle)}&body=${encodeURIComponent(`${text}\n\n${eventUrl}`)}`;
        break;
      default:
        return;
    }

    onShare?.(channel);
    if (channel === Constants.share_channel.email) {
      window.location.href = shareUrl;
    } else {
      window.open(shareUrl, '_blank', 'width=600,height=500');
    }
  };

  return (
    <div className="ShareModal">
      <div className="content">
        <h2>
          {shareType === 'dog'
            ? 'Share this dog profile with your friends!'
            : shareType === 'post'
            ? 'Share this with your friends!'
            : 'Share your show with your friends on social media!'}
        </h2>

        <div className="socialIcons">
          {canNativeShare && (
            <button className="socialIcon native" onClick={handleNativeShare} title="Share…">
              <img src={shareIcon} alt="Share" />
            </button>
          )}
          <button className="socialIcon facebook" onClick={() => handleSocialShare(Constants.share_channel.facebook)} title="Share on Facebook">
            <img src={facebookIcon} alt="Facebook" />
          </button>
          <button className="socialIcon x" onClick={() => handleSocialShare(Constants.share_channel.x)} title="Share on X">
            <img src={xIcon} alt="X" />
          </button>
          <button className="socialIcon whatsapp" onClick={() => handleSocialShare(Constants.share_channel.whatsapp)} title="Share on WhatsApp">
            <img src={whatsappIcon} alt="WhatsApp" />
          </button>
          <button className="socialIcon email" onClick={() => handleSocialShare(Constants.share_channel.email)} title="Share by email">
            <img src={emailIcon} alt="Email" />
          </button>
        </div>

        <div>
          <QRCodeCanvas
            value={eventUrl}
            size={200}
            level="H"
          />
        </div>

        <div className="linkSection">
          <div className="linkLabel">Page Link</div>
          <div className="linkContainer">
            <input
              type="text"
              value={eventUrl}
              readOnly
              className="linkInput"
            />
            <button
              className="copyBtn"
              onClick={handleCopyLink}
              title="Copy link"
            >
              {copySuccess ? '✓' : '📋'}
            </button>
          </div>
          {copySuccess && <div className="copyMessage">Link copied!</div>}
        </div>
      </div>
    </div>
  );
};

export default ShareModal;
